import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setPageOptions } from 'reducers/layout';
import { openPortalRight } from 'reducers/rightToLeftPortal';
// components
import { FormattedMessage } from 'react-intl';
import UserFilter from 'components/UserFilter';
import UserFilterMobile from 'components/UserFilterMobile';
import UserFilterMobileHeader from 'components/UserFilterMobile/UserFilterMobileHeader';
import OfflineContainer from 'components/OfflineScreen/OfflineContainer';
// hooks
import useDidMountEffect from 'hooks/useDidMountEffect';
import { useMediaQuery } from 'react-responsive';
// utils
import cn from 'classnames';
// styles
import styles from '../MySchedule.module.scss';

// Shared My Schedule page chrome: breadcrumbs, mobile filter button/header, desktop filter bar.
// Both schedule views (Pig Movements, Treatments) render their panel inside it.
const ScheduleViewShell = ({
  allOrAnyConditionLabel,
  children,
  fetchData,
  filterGroupName,
  filterSections,
  initialFilter,
  isFiltersTouched,
  isOnline,
  listOptions,
  onUserFilterSectionsChange,
  onUserFilterSegmentSave,
  openPortalRight,
  setBreadcrumbs,
  setPageOptions,
  tableParams,
}) => {
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const userFilter = tableParams.filters?.[0];
  const filterSectionsCount = userFilter?.sections?.length;
  const hasMobileFilterHeader = !isDesktop && filterSectionsCount > 0;

  const handleOpenMobileFilters = () => {
    openPortalRight(
      <UserFilterMobile
        allOrAnyConditionLabel={allOrAnyConditionLabel}
        appliedFilter={tableParams.filters?.[0] || {}}
        filterSections={filterSections}
        filterGroupName={filterGroupName}
        setFilter={(filter) => fetchData({ ...tableParams, filters: filter ? [filter] : undefined })}
      />,
    );
  };

  const handleSetPageOptions = () => {
    setPageOptions({
      rightButton: !isDesktop && isOnline ? {
        icon: 'fa fa-filter',
        onClick: handleOpenMobileFilters,
      } : '',
    });
  };

  useDidMountEffect(handleSetPageOptions, [tableParams, isDesktop, isOnline]);

  useEffect(() => {
    setBreadcrumbs([
      { label: <FormattedMessage id="general.pageTitle.mySchedule" />, useLabelAsMobileTitle: true }
    ]);
    handleSetPageOptions();
    return () => {
      setPageOptions({ rightButton: '' });
    };
  }, []);

  return (
    <div
      className={cn('row', 'centered', styles['my-schedule-page'], {
        [styles['has-mobile-filters']]: hasMobileFilterHeader,
      })}
    >
      <OfflineContainer>
        {hasMobileFilterHeader && (
          <UserFilterMobileHeader
            onClick={handleOpenMobileFilters}
            onClose={() => fetchData({ ...tableParams, filters: undefined })}
            sectionsCount={filterSectionsCount}
            segmentName={userFilter?.name}
          />
        )}
        {isDesktop && (
          <UserFilter
            isVisible
            filterGroupName={filterGroupName}
            availFilterSections={filterSections}
            className={styles.filters}
            listOptions={listOptions}
            onSectionsChange={onUserFilterSectionsChange}
            initialFilter={isFiltersTouched ? tableParams.filters?.[0] : initialFilter}
            onSave={onUserFilterSegmentSave}
          />
        )}
        {children}
      </OfflineContainer>
    </div>
  );
};

ScheduleViewShell.propTypes = {
  allOrAnyConditionLabel: T.node.isRequired,
  children: T.node.isRequired,
  fetchData: T.func.isRequired,
  filterGroupName: T.string.isRequired,
  filterSections: T.array.isRequired,
  initialFilter: T.object,
  isFiltersTouched: T.bool.isRequired,
  isOnline: T.bool.isRequired,
  listOptions: T.array.isRequired,
  onUserFilterSectionsChange: T.func.isRequired,
  onUserFilterSegmentSave: T.func.isRequired,
  openPortalRight: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  tableParams: T.object.isRequired,
};

export default connect(
  (state) => ({
    isOnline: state.network.isOnline,
  }), {
    setBreadcrumbs,
    setPageOptions,
    openPortalRight,
  }
)(ScheduleViewShell);
